import { useState, type FormEvent } from 'react';
import { CheckCircle2 } from 'lucide-react';
import badge from '@/assets/asya-badge.png';
import {
  registrationApplySchema,
  type RegistrationApplyResult,
} from '@asyagrup/group-hub-contracts';
import { api, errorMessage } from '@/lib/api/client';
import { Button, ErrorState, Field, Input } from '@/ui/primitives';

/**
 * Kendi bilgisiyle hesap başvurusu.
 *
 * Bu form hesap AÇMAZ, başvuru gönderir — ve bunu açıkça söyler. Sistem maaş,
 * TCKN ve IBAN tutuyor; adresi bilen herkesin doldurup içeri girebildiği bir
 * form, yetkilendirmeyi "kim kaydolduysa" hâline getirirdi. Rol, kapsam ve
 * şirket onay anında insan kaynakları tarafından verilir.
 *
 * Sorulan alanlar, merkezin başvurandan başka kimseden öğrenemeyeceği şeyler:
 * kimlik numarası ve işe giriş tarihi. İkincisi yıllık izin hakedişini
 * belirliyor (4857 md. 53) ve bugüne kadar İK'nın tek tek sorması gereken
 * şeydi — bu formun asıl kazandırdığı da bu.
 */
export function RegisterPage({ onBack }: { onBack: () => void }) {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    nationalId: '',
    seniorityStartDate: '',
    birthDate: '',
    phone: '',
    jobTitle: '',
    note: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<RegistrationApplyResult | null>(null);

  const set = (key: keyof typeof form) => (value: string) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    // Boş isteğe bağlı alanlar hiç gönderilmez: sözleşme onları `optional`
    // tanımlıyor, boş dizge ise bir değerdir ve doğrulamaya takılır.
    const payload = {
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      nationalId: form.nationalId,
      seniorityStartDate: form.seniorityStartDate,
      ...(form.birthDate ? { birthDate: form.birthDate } : {}),
      ...(form.phone ? { phone: form.phone } : {}),
      ...(form.jobTitle ? { jobTitle: form.jobTitle } : {}),
      ...(form.note ? { note: form.note } : {}),
    };

    const parsed = registrationApplySchema.safeParse(payload);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Bilgileri kontrol edin.');
      return;
    }

    setBusy(true);
    try {
      const result = await api.post<RegistrationApplyResult>('/registration/apply', parsed.data);
      setDone(result);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <Shell>
        <div className="text-center">
          <CheckCircle2 className="text-success mx-auto mb-3" size={32} />
          <h1 className="text-fg text-lg font-semibold">Başvurunuz alındı</h1>
          <p className="text-fg-muted mt-2 text-sm">{done.message}</p>
          <Button className="mt-6 w-full" variant="secondary" onClick={onBack}>
            Giriş ekranına dön
          </Button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <h1 className="text-fg text-lg font-semibold">Üye ol</h1>
      <p className="text-fg-muted mt-1 mb-5 text-sm">
        Bilgilerinizi siz girin, insan kaynakları onayladıktan sonra hesabınız açılır ve giriş
        bilgileriniz size iletilir.
      </p>

      <form onSubmit={onSubmit} className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Ad">
            <Input
              value={form.firstName}
              onChange={(e) => set('firstName')(e.target.value)}
              autoComplete="given-name"
              required
            />
          </Field>
          <Field label="Soyad">
            <Input
              value={form.lastName}
              onChange={(e) => set('lastName')(e.target.value)}
              autoComplete="family-name"
              required
            />
          </Field>
        </div>

        <Field label="Kurumsal e-posta" hint="Giriş adresiniz bu olacak.">
          <Input
            type="email"
            value={form.email}
            onChange={(e) => set('email')(e.target.value)}
            autoComplete="email"
            required
          />
        </Field>

        <Field label="TC kimlik numarası" hint="11 hane. Şifrelenerek saklanır.">
          <Input
            inputMode="numeric"
            value={form.nationalId}
            onChange={(e) => set('nationalId')(e.target.value.replace(/\D/g, '').slice(0, 11))}
            required
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="İşe giriş tarihi" hint="Yıllık izin hakkınız buna göre hesaplanır.">
            <Input
              type="date"
              value={form.seniorityStartDate}
              onChange={(e) => set('seniorityStartDate')(e.target.value)}
              required
            />
          </Field>
          <Field label="Doğum tarihi">
            <Input
              type="date"
              value={form.birthDate}
              onChange={(e) => set('birthDate')(e.target.value)}
            />
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Telefon">
            <Input
              value={form.phone}
              onChange={(e) => set('phone')(e.target.value)}
              autoComplete="tel"
            />
          </Field>
          <Field label="Göreviniz">
            <Input value={form.jobTitle} onChange={(e) => set('jobTitle')(e.target.value)} />
          </Field>
        </div>

        <Field label="Eklemek istediğiniz not">
          <Input value={form.note} onChange={(e) => set('note')(e.target.value)} />
        </Field>

        {error && <ErrorState message={error} />}

        <Button type="submit" className="w-full" disabled={busy}>
          {busy ? 'Gönderiliyor…' : 'Başvuruyu gönder'}
        </Button>
        <Button type="button" variant="ghost" className="w-full" onClick={onBack} disabled={busy}>
          Giriş ekranına dön
        </Button>
      </form>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-bg min-h-dvh">
      <main className="mx-auto flex min-h-dvh max-w-lg items-center px-4 py-10">
        <div className="border-border bg-surface w-full rounded-2xl border p-6 shadow-sm">
          <div className="mb-5 flex items-center gap-2">
            <img src={badge} alt="" aria-hidden className="size-6" />
            <span className="text-fg font-semibold">ASYA GRUP</span>
          </div>
          {children}
        </div>
      </main>
    </div>
  );
}
